import React, { useCallback, useMemo } from 'react';

import { type TBlock } from '@wp-block-builder/package';

import { createFastContext } from '@waypoint/react/fast-store';

import BlocksMapStore, { useBlock, useBlockName } from './BlocksMapStore';
import { Provider as CurrentBlockStoreProvider } from './CurrentBlockStore';

type TTab = 'GENERAL' | 'DATA' | 'STYLES' | 'INSPECT';
type TPreviewMode = 'DESKTOP' | 'MOBILE';
type TMode = 'PREVIEW' | 'EDIT';
type TNavigationBarMode = 'VISIBLE' | 'HIDDEN';
type TState = {
  currentHoverBlockIds: string[];

  currentBlockElement: HTMLElement | null;
  currentScenarioName: null | string;
  currentSidebarTab: TTab;

  navigationBarMode: TNavigationBarMode;
  previewMode: TPreviewMode;
  editMode: TMode;
};

const { Provider, useStore, useStoreRef } = createFastContext<TState>({
  currentHoverBlockIds: [],
  currentBlockElement: null,
  currentScenarioName: null,
  currentSidebarTab: 'GENERAL',
  previewMode: 'DESKTOP',
  editMode: 'EDIT',
  navigationBarMode: 'HIDDEN',
});

type Props = {
  children: Parameters<typeof Provider>[0]['children'];
};
export const WaypointDocumentEditorStoreProvider = ({ children }: Props) => (
  <BlocksMapStore>
    <Provider>
      <CurrentBlockStoreProvider>{children}</CurrentBlockStoreProvider>
    </Provider>
  </BlocksMapStore>
);

export const useDocumentBlock = useBlock;
export const useDocumentBlockName = useBlockName;

export function useCurrentSidebarTab() {
  const [tab, setStore] = useStore((store) => store.currentSidebarTab);
  const setter = useCallback(
    (currentSidebarTab: TTab) => {
      setStore({ currentSidebarTab });
    },
    [setStore]
  );
  return useMemo(() => [tab, setter] as const, [tab, setter]);
}

function generateStateHook<TValue>(name: keyof TState) {
  return () => {
    const [value, setStore] = useStore((store) => store[name] as TValue);
    const setter = useCallback(
      (value: TValue) => {
        setStore({ [name]: value });
      },
      [setStore]
    );
    return useMemo(() => [value, setter] as const, [value, setter]);
  };
}

export const usePreviewMode = generateStateHook<TPreviewMode>('previewMode');
export const useEditMode = generateStateHook<TMode>('editMode');
export const useCurrentScenarioName = generateStateHook<string | null>('currentScenarioName');
export const useNavigationBarMode = generateStateHook<TState['navigationBarMode']>('navigationBarMode');
export const useCurrentBlockElement = generateStateHook<TState['currentBlockElement']>('currentBlockElement');
const useCurrentHoverBlockIds = generateStateHook<TState['currentHoverBlockIds']>('currentHoverBlockIds');

export function useHoverState(block: TBlock): 'none' | 'ancestor' | 'self' {
  const [currentHoverBlockIds] = useCurrentHoverBlockIds();
  return useMemo(() => {
    if (currentHoverBlockIds.length === 0) {
      return 'none';
    }
    if (currentHoverBlockIds[0] === block.id) {
      return 'self';
    }
    if (currentHoverBlockIds.includes(block.id)) {
      return 'ancestor';
    }
    return 'none';
  }, [currentHoverBlockIds, block]);
}

export function useMutateCurrentHover() {
  const store = useStoreRef();
  return useMemo(
    () => ({
      push: (block: TBlock) => {
        store.set({ currentHoverBlockIds: [block.id, ...store.get().currentHoverBlockIds] });
      },
      remove: (blockId: string) => {
        const currentHoverBlockIds = store.get().currentHoverBlockIds.filter((id) => id !== blockId);
        store.set({ currentHoverBlockIds });
      },
    }),
    [store]
  );
}
